import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { UserInvitationsBulkInviteTable } from "./UserInvitationsBulkInviteTable";
import {
  BulkInviteInvitationStatus,
  BulkInvitation,
  useValidateBulkUpload,
  processInvitations,
  createAndSetStatusMessageMap,
  invitationNeedsRevalidation,
  getBulkInviteIdentifierConfigErrors,
  bulkExtraIdentifierColumns,
  type BulkUploadState
} from "./userInvitationUtils";
import { identifierColumn } from "../Networks/identifierUtils";
import {
  useUsersByEmail,
  useUsersByNPI
} from "../Users/userUtils";
import { useAppSelector } from "../../app/hooks";
import {
  selectCurrentUser,
  selectUserRoles
} from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import {
  InstitutionNotifications,
  alphabetizeInstitutions
} from "../Institutions/institutionUtils";
import {
  ProgramNotifications,
  alphabetizePrograms,
  programLabel
} from "../Programs/programUtils";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { useProgramsIdentifierConfig } from "../Networks/networkUtils";
import { ErrorPage } from "../../common-ui/ErrorPage";
import { selectProgramsStatus } from "../Programs/programsSlice";
import {
  useTrainingStages,
  TrainingStage
} from "../TrainingStages/trainingStagesAPI";
import { DynamicBulkInviteButton } from "./DynamicBulkInviteButton";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";

const pageDetails: PageWrapperProps = {
  className: "UserInvitationsBulkInvite",
  headerText: "User Invitations - Bulk Invite "
};

export const UserInvitationsBulkInvite = () => {
  // Extract parsed file results from navigation (mapped in useEffect)
  const { state } = useLocation();
  const upload = state as BulkUploadState | null;
  const uploadedInvitations = upload?.invitations;
  const navigate = useNavigate();

  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUserRoles = useAppSelector(selectUserRoles);

  const [invitations, setInvitations] = useState<BulkInvitation[] | undefined>(
    uploadedInvitations
  );
  const [statusMessages, setStatusMessages] = useState<
    Map<string, BulkInviteInvitationStatus | string>
  >(new Map());
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string>();
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [allEntriesAreValid, setAllEntriesAreValid] = useState(false);
  const [allInvitesSent, setAllInvitesSent] = useState(false);
  const [isProcessingInvitations, setIsProcessingInvitations] = useState(false);
  const [rowsAwaitingRevalidation, setRowsAwaitingRevalidation] = useState<
    Set<string>
  >(new Set());

  const allProgramsStatus = useAppSelector(selectProgramsStatus);
  const statusMessagesEntries = statusMessages?.entries();

  const {
    canAdministerMultiplePrograms,
    canAdministerMultipleInstitutions,
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  // Decide whether BOTH pgy + yearInStage columns must stay visible. Rows can
  // target a program per-row (Program ID column) or via the dropdown, so resolve
  // each involved program's stage. Force both columns if ANY involved program is
  // Fellowship (needs both) or can't be resolved (don't hide what we're unsure
  // of). Only an all-known-non-fellowship upload may hide the column its CSV
  // didn't use.
  const { trainingStages } = useTrainingStages();
  const stageForProgramId = (
    programId?: string
  ): TrainingStage | undefined => {
    if (!programId) return undefined;
    const prog = programsCurrentUserCanAdminister?.find(
      (p) => p.id === programId
    );
    return trainingStages.find((s) => s.id === (prog as any)?.trainingStageId);
  };
  const stageCodeForProgramId = (programId?: string): string | undefined =>
    stageForProgramId(programId)?.code;
  const involvedProgramIds = Array.from(
    new Set(
      (uploadedInvitations ?? [])
        .map((i) =>
          i?.programId && i.programId.length > 0
            ? i.programId
            : selectedProgramId
        )
        .filter((p): p is string => !!p)
    )
  );
  const forceBothYearAndPgyColumns =
    involvedProgramIds.length === 0 ||
    involvedProgramIds.some((pid) => {
      const code = stageCodeForProgramId(pid);
      return code === undefined || code === "FELLOWSHIP";
    });

  const alphabetizedInstitutions = institutionsCurrentUserCanAdminister
    ? alphabetizeInstitutions(institutionsCurrentUserCanAdminister)
    : institutionsCurrentUserCanAdminister;

  const currentUsersProgram = !canAdministerMultiplePrograms
    ? programsCurrentUserCanAdminister?.[0]
    : undefined;
  const currentUsersProgramId = currentUsersProgram?.id;
  const currentUsersInstitution = !canAdministerMultipleInstitutions
    ? institutionsCurrentUserCanAdminister?.[0]
    : undefined;
  const currentUsersInstitutionId = currentUsersInstitution?.id;

  const isProgramAdmin =
    currentUserRoles?.includes(Role.ProgramAdmin) ?? false;

  const { status: usersByEmailStatus, getUsersByEmail } = useUsersByEmail();
  const { status: usersByNPIStatus, getUsersByNPI } = useUsersByNPI();
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  // default to true to avoid flashing the error screen when the flag is true
  const allowBulkInvite =
    flagsStatus === "succeeded" ? allow_bulk_invite : true;

  const invitationsWithProgramId =
    invitations?.map((invitation) => {
      const programIdFromFile = invitation.programId;
      // Use the programId in the file if it exists, otherwise use the selected programId
      const newProgramId =
        programIdFromFile && programIdFromFile.length > 0
          ? programIdFromFile
          : selectedProgramId ?? currentUsersProgramId;
      invitation.programId = newProgramId;
      return invitation;
    }) ?? [];

  // Identifier config for every program involved (per-row programId or the
  // dropdown selection). Drives config-driven inline identifier validation in the
  // review grid so required/regex feedback is immediate and correct for any
  // network's config — mirroring the backend validate-bulk-upload validator.
  const identifierProgramIds = Array.from(
    new Set(
      (invitations ?? [])
        .map((i) =>
          i?.programId && i.programId.length > 0
            ? i.programId
            : selectedProgramId
        )
        .filter((p): p is string => !!p)
    )
  );
  const {
    configs: identifierConfigsByProgram,
    status: identifierConfigStatus
  } = useProgramsIdentifierConfig(identifierProgramIds);

  // Config-authoritative column visibility for the review grid — mirrors the
  // template download + New/Edit User forms: an identifier column shows when some
  // involved network collects it. Identifier VALUES are user-level, so a network
  // that doesn't collect LEARN simply doesn't ask for it here (we hide the empty
  // column) rather than forbidding the value. Crucially, we ALSO show the column
  // whenever any row actually has a value — never hide data a user provided; they
  // must be able to see and edit it (it's still written on invite). SysAdmin sees
  // all columns (matching the all-columns SysAdmin template). While config is
  // still loading, show the columns so nothing is hidden mid-load.
  const isSysAdmin = currentUserRoles?.includes(Role.SystemAdmin) ?? false;
  const declaredIdentifierKeys = new Set<string>();
  for (const pid of Object.keys(identifierConfigsByProgram)) {
    const cfg = identifierConfigsByProgram[pid];
    for (const role of Object.keys(cfg.identifiersByRole)) {
      for (const it of cfg.identifiersByRole[role]) {
        declaredIdentifierKeys.add(it.key);
      }
    }
  }
  const configResolved = identifierConfigStatus === "succeeded";
  const anyNpiValue = !!invitations?.some(
    (i) => i.npi != null && String(i.npi).trim() !== ""
  );
  const anyLearnValue = !!invitations?.some(
    (i) => i.simplLearnId != null && String(i.simplLearnId).trim() !== ""
  );
  const showNpiColumn =
    isSysAdmin ||
    anyNpiValue ||
    !configResolved ||
    declaredIdentifierKeys.has("npi");
  const showLearnColumn =
    isSysAdmin ||
    anyLearnValue ||
    !configResolved ||
    declaredIdentifierKeys.has("appd_learn_id");

  // SE-3118: any other identifier gets its own column when an involved network
  // declares it or a row has a value for it (labelled with its display name).
  const extraIdentifierColumns = new Map<string, string>();
  for (const cfg of Object.values(identifierConfigsByProgram)) {
    for (const items of Object.values(cfg.identifiersByRole)) {
      for (const it of items) {
        if (it.key === "npi" || it.key === "appd_learn_id") continue;
        extraIdentifierColumns.set(identifierColumn(it.key), it.displayName);
      }
    }
  }
  for (const row of invitations ?? []) {
    for (const column of bulkExtraIdentifierColumns(row)) {
      if (!extraIdentifierColumns.has(column)) {
        extraIdentifierColumns.set(column, column);
      }
    }
  }

  const {
    status: validateBulkUploadStatus,
    invitations: validatedInvitations,
    validateBulkUpload
  } = useValidateBulkUpload();

  const missingNeededProgram =
    (!canAdministerMultiplePrograms && !currentUsersProgram) ||
    !selectedProgramId;

  const showMissingProgramWarning =
    allProgramsStatus != "loading" &&
    !canAdministerMultiplePrograms &&
    !currentUsersProgram;

  const programAdminInviteMessage =
    isProgramAdmin &&
    !canAdministerMultiplePrograms &&
    currentUsersInstitution &&
    currentUsersProgram
      ? `Users will be added to ${programLabel({
          name: currentUsersProgram.name,
          institution: currentUsersInstitution,
        })}`
      : "";

  const missingNeededInstitution =
    (!canAdministerMultiplePrograms && !currentUsersInstitution) ||
    !selectedInstitutionId;
  const showMissingInstitutionWarning =
    allProgramsStatus != "loading" &&
    !canAdministerMultiplePrograms &&
    !currentUsersInstitution;

  const alphabetizedProgramsInSelectedInstitution = alphabetizePrograms(
    programsCurrentUserCanAdminister?.filter(
      (program) => program.institutionId === selectedInstitutionId
    ) ?? []
  );

  const anyApiIsLoading = [
    validateBulkUploadStatus,
    allProgramsStatus,
    usersByEmailStatus,
    usersByNPIStatus
  ].includes("loading");

  const allNecessarySelectionsMade = () => {
    if (canAdministerMultipleInstitutions && !selectedInstitutionId)
      return false;
    if (canAdministerMultiplePrograms && !selectedProgramId) return false;
    if (missingNeededInstitution) return false;
    if (missingNeededProgram) return false;
    return true;
  };

  const handleSelectedInstitutionChange = (event: SelectChangeEvent) => {
    setSelectedProgramId("");
    setSelectedInstitutionId(event.target.value);
  };

  const handleSelectedProgramChange = (event: SelectChangeEvent) => {
    setSelectedProgramId(event.target.value);
  };

  // Set status messages after validating the invitations
  useEffect(() => {
    if (validatedInvitations && validatedInvitations.length > 0) {
      setInvitations(validatedInvitations);
      setRowsAwaitingRevalidation((prev) => {
        if (prev.size === 0) return prev;
        const updated = new Set(prev);
        validatedInvitations.forEach((invitation) => {
          if (!invitation.requestId) return;
          if (!updated.has(invitation.requestId)) return;
          const hasDuplicateErrors = invitation.errors?.some((error) =>
            error.error?.toLowerCase?.().includes("duplicate")
          );
          if (!hasDuplicateErrors) {
            updated.delete(invitation.requestId);
          }
        });
        return updated;
      });
    }
  }, [validatedInvitations]);

  useEffect(() => {
    createAndSetStatusMessageMap(
      invitations,
      setStatusMessages,
      rowsAwaitingRevalidation,
      validateBulkUploadStatus === "failed"
    );

    const invitationsHaveErrors = !!invitations?.some(
      (invitation) => !!invitation.errors
    );

    // Defense-in-depth: also fail closed on any config-driven identifier error
    // (required/regex per the row's network config), independent of the backend
    // response. This guarantees a required identifier is never marked "Ready to
    // Invite" even if the backend validator is stale/unavailable.
    const invitationsHaveConfigErrors = !!invitations?.some(
      (invitation) =>
        Object.keys(
          getBulkInviteIdentifierConfigErrors(
            invitation,
            identifierConfigsByProgram,
            selectedProgramId
          )
        ).length > 0
    );

    const invitationsNeedRevalidation = !!invitations?.some((invitation) => {
      const validatedInvitation = validatedInvitations?.find(
        (vi) => vi.requestId === invitation.requestId
      );
      return invitationNeedsRevalidation(invitation, validatedInvitation);
    });
    setAllEntriesAreValid(
      !invitationsHaveErrors &&
        !invitationsHaveConfigErrors &&
        !invitationsNeedRevalidation &&
        // Fail closed: if the validation request itself failed (e.g. WAF 403 /
        // network error), rows have no errors attached and would otherwise look
        // "Ready to Invite". Never allow inviting when validation did not run.
        validateBulkUploadStatus !== "failed"
    );
  }, [
    invitations,
    validatedInvitations,
    rowsAwaitingRevalidation,
    validateBulkUploadStatus,
    identifierConfigsByProgram,
    selectedProgramId
  ]);

  useEffect(() => {
    if (currentUsersProgramId) {
      setSelectedProgramId(currentUsersProgramId);
    }
  }, [currentUsersProgramId]);

  useEffect(() => {
    if (statusMessages && statusMessages.size > 0) {
      let allInvited = true;
      statusMessages.forEach((status) => {
        if (status !== BulkInviteInvitationStatus.Sent) {
          allInvited = false;
        }
      });
      setAllInvitesSent(allInvited);
    }
  }, [statusMessagesEntries]);

  useEffect(() => {
    if (currentUsersInstitutionId) {
      setSelectedInstitutionId(currentUsersInstitutionId);
    }
  }, [currentUsersInstitutionId]);

  useEffect(() => {
    // For SysAdmins with multiple institutions, both program and institution must be selected
    // For Program Admins or SysAdmins with single institution, only program needs to be selected
    const shouldValidate =
      selectedProgramId &&
      (canAdministerMultipleInstitutions ? (selectedInstitutionId !== '' && selectedInstitutionId != null) : true);
    if (shouldValidate) {
      validateBulkUpload(invitationsWithProgramId);
    }
  }, [selectedProgramId, selectedInstitutionId, canAdministerMultipleInstitutions]);

  if (!allowBulkInvite) {
    return <ErrorPage />;
  }

  // If user is a programAdmin, make sure they have an institution and a program associated.
  // If they don't, display an error(s) and disable the submit button.
  // If they do, show the Intitution Name > Program Name for reference.

  // If user is a sysadmin, no need to check for associated programs or instituions
  // Show a dropdown of instutions for selection > a dropdown of programs for selection
  // The dropdown for programs needs to be disabled until an institution is selected and the programs are loaded/filtered.
  // If the selected institution has no programs, display an error and keep everything disabled.

  // TODO: Not MVP!! -> If the user is an instAdmin, make sure they have an institution associated.
  // If they don't, display an error and disable the submit button.
  // If they do, show the Institution Name > a dropdown of programs in the institution for selection.
  return (
    <PageWrapper {...pageDetails}>
      <Grid xs={12} spacing={2} container>
        {!!upload?.ignoredColumns?.length && (
          <Grid xs={12}>
            <Alert severity="warning">
              {t(
                "These columns were ignored because they aren't in the template: {{columns}}",
                { columns: upload.ignoredColumns.join(", ") }
              )}
            </Alert>
          </Grid>
        )}
        {canAdministerMultipleInstitutions && (
          <>
            <Grid xs={12}>
              <Grid xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel
                    id="bulk-invite-institution-label"
                    required
                    disabled={anyApiIsLoading}
                  >
                    {t("Institution")}
                  </InputLabel>
                  <Select
                    labelId="bulk-invite-institution-label"
                    data-testid="bulk-invite-institution-input-group"
                    id="selectedInstitutionId"
                    value={selectedInstitutionId}
                    label={t("Institution")}
                    onChange={handleSelectedInstitutionChange}
                    required
                    disabled={anyApiIsLoading}
                  >
                    {alphabetizedInstitutions?.map((institution) => (
                      <MenuItem value={institution.id} key={institution.id}>
                        {institution.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(!institutionsCurrentUserCanAdminister ||
                  institutionsCurrentUserCanAdminister.length < 1) && (
                  <Alert severity="warning" sx={{ marginTop: 1 }}>
                    {t(InstitutionNotifications.NoInstitutions)}
                  </Alert>
                )}
              </Grid>
            </Grid>
          </>
        )}
        {canAdministerMultiplePrograms && (
          <>
            <Grid xs={12}>
              <Grid xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel
                    id="bulk-invite-program-label"
                    required
                    disabled={anyApiIsLoading || !selectedInstitutionId}
                  >
                    {t("Program")}
                  </InputLabel>
                  <Select
                    labelId="bulk-invite-program-label"
                    data-testid="bulk-invite-program-input-group"
                    id="selectedProgramId"
                    value={selectedProgramId}
                    label={t("Program")}
                    onChange={handleSelectedProgramChange}
                    required
                    disabled={anyApiIsLoading || !selectedInstitutionId}
                  >
                    <MenuItem value={""} key={"no-program"}>
                      {t("(none)")}
                    </MenuItem>
                    {alphabetizedProgramsInSelectedInstitution?.map(
                      (program) => {
                        return (
                          <MenuItem value={program.id} key={program.id}>
                            {program.name}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
                {selectedInstitutionId &&
                  (!alphabetizedProgramsInSelectedInstitution ||
                    alphabetizedProgramsInSelectedInstitution.length < 1) && (
                    <Alert severity="warning" sx={{ marginTop: 1 }}>
                      {t(ProgramNotifications.NoProgramsInInstitution)}
                    </Alert>
                  )}
              </Grid>
            </Grid>
          </>
        )}

        {/* Dynamic Validate/Invite Button */}
        {/* Should be "Validate" until all rows have "Ready to invite" status */}
        <Grid xs={12} display="flex" flexDirection="row">
          <Grid>
            <DynamicBulkInviteButton
              allEntriesAreValid={allEntriesAreValid}
              allInvitesSent={allInvitesSent}
              necessarySelectionsMade={
                !missingNeededInstitution && !missingNeededProgram
              }
              revalidate={() => validateBulkUpload(invitationsWithProgramId)}
              invite={() =>
                processInvitations({
                  validatedInvitations: invitations,
                  statusMessages,
                  selectedProgramId,
                  currentUsersProgramId,
                  currentUser,
                  setStatusMessages,
                  setIsProcessing: setIsProcessingInvitations
                })
              }
              loading={anyApiIsLoading || isProcessingInvitations}
              validating={validateBulkUploadStatus === "loading"}
            />
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => navigate("/invitations/bulkupload")}
            >
              {t("Cancel")}
            </Button>
          </Grid>
        </Grid>

        {/* Validation request failed (e.g. blocked or network error) — fail closed */}
        {validateBulkUploadStatus === "failed" && (
          <Grid xs={12}>
            <Alert severity="error" sx={{ marginTop: 1 }}>
              {t(
                "We couldn't validate this list, so no one has been marked ready to invite. Please press 'Validate' to try again. If the list is large, try splitting it into smaller files."
              )}
            </Alert>
          </Grid>
        )}

        {/* Dynamic instructions */}
        <Grid xs={12}>
          <Typography>
            {t(
              "Press 'Validate' to check the bulk invite list for any errors. Each user on the list must be successfully validated, indicated by a 'Ready to Invite' status, before you can proceed."
            )}
          </Typography>
        </Grid>

        {showMissingInstitutionWarning && (
          <Grid xs={12}>
            <Grid xs={12} sm={4}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(
                  "You do not have a necessary institution associated with your account. Please contact your system administrator."
                )}
              </Alert>
            </Grid>
          </Grid>
        )}
        {showMissingProgramWarning && (
          <Grid xs={12}>
            <Grid xs={12} sm={4}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t(
                  "You do not have a necessary program associated with your account. Please contact your system administrator."
                )}
              </Alert>
            </Grid>
          </Grid>
        )}

        {/* Display a warning if the user needs to select an Institution */}
        {canAdministerMultipleInstitutions && !selectedInstitutionId && (
          <Grid xs={12}>
            <Grid xs={12} sm={4}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t("Please select an institution from the dropdown above.")}
              </Alert>
            </Grid>
          </Grid>
        )}

        {/* Display a warning if the user needs to select a Program */}
        {canAdministerMultiplePrograms && !selectedProgramId && (
          <Grid xs={12}>
            <Grid xs={12} sm={4}>
              <Alert severity="warning" sx={{ marginTop: 1 }}>
                {t("Please select a program from the dropdown above.")}
              </Alert>
            </Grid>
          </Grid>
        )}

        {/* Only show the table once all necessary selections have been made */}
        {allNecessarySelectionsMade() && (
          <Grid xs={12} sx={{ height: 500 }}>
            {/* Show what institution and program users will be added to if acting as a programAdmin. */}
            {isProgramAdmin && programAdminInviteMessage}
            <UserInvitationsBulkInviteTable
              validatedInvitations={validatedInvitations || []}
              currentInvitations={invitations || []}
              statusMessages={statusMessages}
              setStatusMessages={setStatusMessages}
              setInvitations={setInvitations}
              loading={anyApiIsLoading}
              currentUserRoles={currentUserRoles}
              hasCustomProgramId={
                !!uploadedInvitations?.find((i) => !!i.programId)
              }
              csvHasPgyColumn={
                !!uploadedInvitations?.some(
                  (i) => i != null && "pgy" in i
                )
              }
              csvHasYearInStageColumn={
                !!uploadedInvitations?.some(
                  (i) => i != null && "yearInStage" in i
                )
              }
              forceBothYearAndPgyColumns={forceBothYearAndPgyColumns}
              getStageForProgramId={stageForProgramId}
              getUsersByEmail={getUsersByEmail}
              getUsersByNPI={getUsersByNPI}
              selectedInstitutionId={selectedInstitutionId}
              selectedProgramId={selectedProgramId}
              identifierConfigsByProgram={identifierConfigsByProgram}
              showNpiColumn={showNpiColumn}
              showLearnColumn={showLearnColumn}
              extraIdentifierColumns={Array.from(
                extraIdentifierColumns,
                ([field, title]) => ({ field, title })
              )}
              onRowDelete={(
                updatedInvitations,
                deletedInvitation,
                options
              ) => {
                if (deletedInvitation?.requestId) {
                  setRowsAwaitingRevalidation((prev) => {
                    if (!prev.has(deletedInvitation.requestId!)) return prev;
                    const updated = new Set(prev);
                    updated.delete(deletedInvitation.requestId!);
                    return updated;
                  });
                }
                if (options?.skipValidation) {
                  setInvitations(updatedInvitations);
                  return;
                }
                // Trigger revalidation after row deletion to update duplicate errors
                const invitationsWithProgramId = updatedInvitations.map(
                  (invitation) => {
                    const programIdFromFile = invitation.programId;
                    const newProgramId =
                      programIdFromFile && programIdFromFile.length > 0
                        ? programIdFromFile
                        : selectedProgramId ?? currentUsersProgramId;
                    return {
                      ...invitation,
                      programId: newProgramId
                    };
                  }
                );
                validateBulkUpload(invitationsWithProgramId);
              }}
              rowsAwaitingRevalidation={rowsAwaitingRevalidation}
              setRowsAwaitingRevalidation={setRowsAwaitingRevalidation}
            />
          </Grid>
        )}
      </Grid>
    </PageWrapper>
  );
};
